import {
  CreatePickupRequest,
  PickupRequestDocument,
  UpdatePickupRequest,
} from "../../../types";

import { PendingDocument } from "./documents";

export interface CreatePickupRequestResponse {
  requestNumber: string;
}

export interface UploadPickupRequestDocumentParams {
  requestNumber: string;
  file: File;
  description?: string;
}

export interface SubmitPickupRequestApi {
  createPickupRequest: (
    payload: CreatePickupRequest
  ) => Promise<CreatePickupRequestResponse>;
  uploadPickupRequestDocument: (
    params: UploadPickupRequestDocumentParams
  ) => Promise<PickupRequestDocument>;
}

export interface DocumentUploadFailure {
  document: PendingDocument;
  message: string;
}

export interface SubmitPickupRequestResult {
  requestNumber: string;
  failedUploads: DocumentUploadFailure[];
}

const getUploadErrorMessage = (reason: unknown): string => {
  const message = (reason as { response?: { data?: { message?: string } } })
    ?.response?.data?.message;

  return message || (reason as { message?: string })?.message || "";
};

// Sequential, not Promise.all: the service caps the total size across a request by adding the
// incoming file to the ones already stored, and parallel uploads all read the same pre-upload
// total, so a batch can settle above the cap.
const uploadDocuments = async (
  requestNumber: string,
  documents: PendingDocument[],
  uploadPickupRequestDocument: SubmitPickupRequestApi["uploadPickupRequestDocument"]
): Promise<DocumentUploadFailure[]> => {
  const failedUploads: DocumentUploadFailure[] = [];

  for (let index = 0; index < documents.length; index += 1) {
    const document = documents[index];

    try {
      // eslint-disable-next-line no-await-in-loop
      await uploadPickupRequestDocument({
        requestNumber,
        file: document.file,
        description: document.description,
      });
    } catch (reason) {
      failedUploads.push({
        document,
        message: getUploadErrorMessage(reason),
      });
    }
  }

  return failedUploads;
};

// CreatePickupRequestDto has no field for documents — a request must exist before a document
// can be attached to it, so the create call is awaited first and its requestNumber threads
// into every upload. A rejected upload never rolls back the (already persisted) request; it is
// reported back as a per-document failure instead.
export const submitPickupRequest = async (
  payload: CreatePickupRequest,
  documents: PendingDocument[],
  api: SubmitPickupRequestApi
): Promise<SubmitPickupRequestResult> => {
  const created = await api.createPickupRequest(payload);
  const failedUploads = await uploadDocuments(
    created.requestNumber,
    documents,
    api.uploadPickupRequestDocument
  );

  return { requestNumber: created.requestNumber, failedUploads };
};

export interface SubmitPickupRequestEditApi {
  updatePickupRequest: (
    requestNumber: string,
    payload: UpdatePickupRequest
  ) => Promise<void>;
  uploadPickupRequestDocument: (
    params: UploadPickupRequestDocumentParams
  ) => Promise<PickupRequestDocument>;
  deleteDocument: (requestNumber: string, documentId: number) => Promise<void>;
}

export interface SubmitPickupRequestEditResult {
  failedUploads: DocumentUploadFailure[];
  failedDeleteDocumentIds: number[];
}

// The PUT is awaited first, same reasoning as create: a document change is meaningless against
// a request whose field edit never landed. Deletes run before uploads: the server caps total
// size by adding the incoming file to what is already stored, so an upload ahead of a delete on
// the same request is checked against a total that is about to shrink.
export const submitPickupRequestEdit = async (
  requestNumber: string,
  payload: UpdatePickupRequest,
  addedDocuments: PendingDocument[],
  removedDocumentIds: number[],
  api: SubmitPickupRequestEditApi
): Promise<SubmitPickupRequestEditResult> => {
  await api.updatePickupRequest(requestNumber, payload);

  const failedDeleteDocumentIds: number[] = [];

  for (let index = 0; index < removedDocumentIds.length; index += 1) {
    const documentId = removedDocumentIds[index];

    try {
      // eslint-disable-next-line no-await-in-loop
      await api.deleteDocument(requestNumber, documentId);
    } catch {
      failedDeleteDocumentIds.push(documentId);
    }
  }

  const failedUploads = await uploadDocuments(
    requestNumber,
    addedDocuments,
    api.uploadPickupRequestDocument
  );

  return { failedUploads, failedDeleteDocumentIds };
};
